import { FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
	faCheck,
	faCircleCheck,
	faTriangleExclamation,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Icon, Modal } from "@/components";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";
import { classNames } from "@/utils/classNames";

import {
	useGetZohoCompanySyncTriggerQuery,
	useUpdateZohoCompanySyncTriggerMutation,
	useGetZohoCompanyDealCreationTriggerQuery,
	useUpdateZohoCompanyDealCreationTriggerMutation,
	useGetZohoConversationSyncQuery,
	useUpdateZohoConversationSyncMutation,
	useLazyGetZohoOAuthConnectUrlQuery,
	useGetZohoOAuthStatusQuery,
	useDisconnectZohoOAuthMutation,
} from "@/pages/Private/redux/zoho/zoho.api";

import { ZohoSyncTrigger, ZohoDealCreationTrigger } from "../schema/zoho";
import { ZohoFieldMappingModal } from "./ZohoFieldMappingModal";
import { ZohoDealConfigTab } from "./ZohoDealConfigTab";
import { ZohoUserMappingTab } from "./ZohoUserMappingTab";
import { ZohoSyncLogsTab } from "./ZohoSyncLogsTab";
import { ZohoResyncSection } from "./ZohoResyncSection";
import { CrmReconnectBanner } from "./CrmReconnectBanner";

enum ZohoTabs {
	CONNECTION = "CONNECTION",
	SETTINGS = "SETTINGS",
	DEAL_CONFIGURATION = "DEAL_CONFIGURATION",
	USER_MAPPING = "USER_MAPPING",
	SYNC_LOGS = "SYNC_LOGS",
}

export const ZohoSettings: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.zoho.${key}`);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [searchParams, setSearchParams] = useSearchParams();
	const currentTab = (searchParams.get("zohoTab") as ZohoTabs) || ZohoTabs.CONNECTION;

	const [isFieldMappingOpen, setIsFieldMappingOpen] = useState(false);
	const [showDisconnectModal, setShowDisconnectModal] = useState(false);

	// OAuth hooks
	const [getOAuthConnectUrl, { isLoading: isLoadingOAuthUrl }] =
		useLazyGetZohoOAuthConnectUrlQuery();
	const { data: oauthStatus, refetch: refetchOAuthStatus } = useGetZohoOAuthStatusQuery();
	const [disconnectOAuth, { isLoading: isDisconnecting }] = useDisconnectZohoOAuthMutation();

	const isConnected = oauthStatus?.connected ?? false;
	// The provider refused the stored sign-in: nothing syncs until the company reconnects (PREC-4353).
	const needsReauthorization = isConnected && Boolean(oauthStatus?.needsReauthorization);

	// Sync Trigger hooks
	const { data: companySyncTrigger } = useGetZohoCompanySyncTriggerQuery(undefined, {
		skip: !isConnected,
	});
	const [updateCompanySyncTrigger] = useUpdateZohoCompanySyncTriggerMutation();

	// Deal Creation Trigger hooks
	const { data: companyDealCreationTrigger } = useGetZohoCompanyDealCreationTriggerQuery(
		undefined,
		{ skip: !isConnected }
	);
	const [updateCompanyDealCreationTrigger] = useUpdateZohoCompanyDealCreationTriggerMutation();

	// Conversation Sync hooks
	const { data: conversationSync } = useGetZohoConversationSyncQuery(undefined, {
		skip: !isConnected,
	});
	const [updateConversationSync] = useUpdateZohoConversationSyncMutation();

	const handleTabChange = useCallback(
		(tab: ZohoTabs) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					next.set("zohoTab", tab);

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const syncTriggerOptions: DropdownAutoCompleteItem[] = [
		{ id: ZohoSyncTrigger.DISABLED, title: ts("syncTrigger.DISABLED") },
		{ id: ZohoSyncTrigger.ON_CAMPAIGN_START, title: ts("syncTrigger.ON_CAMPAIGN_START") },
		{ id: ZohoSyncTrigger.ON_REPLY, title: ts("syncTrigger.ON_REPLY") },
	];

	const handleSyncTriggerChange = async (value: DropdownAutoCompleteItem) => {
		try {
			await updateCompanySyncTrigger({
				trigger: value.id as ZohoSyncTrigger,
			}).unwrap();
		} catch (error) {
			console.error("Failed to update sync trigger:", error);
		}
	};

	const dealCreationTriggerOptions: DropdownAutoCompleteItem[] = [
		{ id: ZohoDealCreationTrigger.DISABLED, title: ts("dealCreationTrigger.DISABLED") },
		{
			id: ZohoDealCreationTrigger.ON_RESPONSE,
			title: ts("dealCreationTrigger.ON_RESPONSE"),
		},
		{
			id: ZohoDealCreationTrigger.ON_POSITIVE_RESPONSE,
			title: ts("dealCreationTrigger.ON_POSITIVE_RESPONSE"),
		},
	];

	const handleDealCreationTriggerChange = async (value: DropdownAutoCompleteItem) => {
		try {
			await updateCompanyDealCreationTrigger({
				trigger: value.id as ZohoDealCreationTrigger,
			}).unwrap();
		} catch (error) {
			console.error("Failed to update deal creation trigger:", error);
		}
	};

	const handleConversationSyncToggle = async () => {
		const newValue = !(conversationSync?.enabled ?? false);

		try {
			await updateConversationSync({ enabled: newValue }).unwrap();
			handleOpenSnackbar(ts("conversationSync.success"));
		} catch {
			handleOpenSnackbar(ts("conversationSync.error"));
		}
	};

	const handleConnectOAuth = async () => {
		try {
			const result = await getOAuthConnectUrl().unwrap();

			window.location.href = result.url;
		} catch {
			handleOpenSnackbar(ts("oauth.connectError"));
		}
	};

	const handleDisconnectOAuth = async () => {
		try {
			await disconnectOAuth().unwrap();
			setShowDisconnectModal(false);
		} catch (error) {
			const status = (error as { status?: number })?.status;

			if (status === 400) {
				setShowDisconnectModal(false);
				handleOpenSnackbar(ts("oauth.alreadyDisconnected"));
				refetchOAuthStatus();

				return;
			}

			console.error("Failed to disconnect OAuth:", error);
			handleOpenSnackbar(ts("oauth.disconnectError"));
		}
	};

	const tabItems = [
		{ key: ZohoTabs.CONNECTION, label: ts("tabs.connection") },
		{ key: ZohoTabs.SETTINGS, label: ts("tabs.settings") },
		{ key: ZohoTabs.DEAL_CONFIGURATION, label: ts("tabs.dealConfiguration") },
		{ key: ZohoTabs.USER_MAPPING, label: ts("tabs.userMapping") },
		{ key: ZohoTabs.SYNC_LOGS, label: ts("tabs.syncLogs") },
	];

	const connectionContent = (
		<div className="flex flex-col gap-4">
			{isConnected ? (
				<>
					<div className="flex items-center gap-3 p-4 rounded-lg border border-status-success bg-green-50">
						<Icon className="w-5 h-5 text-status-success" icon={faCircleCheck} />
						<div className="flex flex-col">
							<span className="text-sm font-semibold text-status-success">
								{ts("oauth.connectedViaOAuth")}
							</span>
							{oauthStatus?.zohoOrgId && (
								<span className="text-xs text-gray-500">
									{ts("oauth.zohoOrgId")}: {oauthStatus.zohoOrgId}
								</span>
							)}
							<span className="text-xs text-gray-400">{ts("oauth.tokenRefresh")}</span>
						</div>
					</div>
					<div className="flex flex-row gap-2">
						<div className="flex flex-wrap gap-2 w-fit">
							<Button
								color={ButtonColor.DANGER_SECONDARY}
								isLoading={isDisconnecting}
								size={ButtonSize.S}
								title={ts("disconnect")}
								onClick={() => setShowDisconnectModal(true)}
							/>
						</div>
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title={ts("configureFieldMapping")}
								onClick={() => setIsFieldMappingOpen(true)}
							/>
						</div>
					</div>
				</>
			) : (
				<div className="flex flex-col gap-2 w-fit">
					<Button
						className="max-w-[250px]"
						color={ButtonColor.ACTION}
						isLoading={isLoadingOAuthUrl}
						size={ButtonSize.S}
						title={ts("oauth.connectWithZoho")}
						onClick={handleConnectOAuth}
					/>
				</div>
			)}
		</div>
	);

	const settingsContent = (
		<div className="flex flex-col w-full">
			<div className="text-sm text-gray-600 mb-3">{ts("syncTrigger.description")}</div>
			<div className="w-full max-w-[400px]">
				<Dropdown
					data={syncTriggerOptions}
					defaultValue={
						companySyncTrigger
							? {
									id: companySyncTrigger.trigger,
									title: ts(`syncTrigger.${companySyncTrigger.trigger}`),
								}
							: undefined
					}
					floating={true}
					handleSelect={handleSyncTriggerChange}
					label={ts("syncTrigger.title")}
				/>
			</div>
			<div className="text-sm text-gray-600 mb-3 mt-6">{ts("dealCreationTrigger.description")}</div>
			<div className="w-full max-w-[400px]">
				<Dropdown
					data={dealCreationTriggerOptions}
					defaultValue={
						companyDealCreationTrigger
							? {
									id: companyDealCreationTrigger.trigger,
									title: ts(`dealCreationTrigger.${companyDealCreationTrigger.trigger}`),
								}
							: undefined
					}
					floating={true}
					handleSelect={handleDealCreationTriggerChange}
					label={ts("dealCreationTrigger.title")}
				/>
			</div>
			<div className="mt-6 pt-6 border-t border-gray-200">
				<div className="text-sm font-medium text-gray-900 mb-2">{ts("conversationSync.title")}</div>
				<Checkbox
					isChecked={conversationSync?.enabled ?? false}
					name="conversationSync"
					onChange={handleConversationSyncToggle}
				>
					{ts("conversationSync.label")}
				</Checkbox>
				<div className="text-sm text-gray-600 mt-2">{ts("conversationSync.description")}</div>
			</div>
			<ZohoResyncSection />
		</div>
	);

	return (
		<>
			<div className="p-4 rounded-xl border border-gray-200 bg-brand-white">
				<div className="flex items-center justify-between mb-4">
					<div className="text-smd">{ts("title")}</div>
					{needsReauthorization ? (
						<div className="flex items-center gap-1 text-sm text-status-warning">
							<Icon className="w-4 h-4" icon={faTriangleExclamation} />
							{ts("oauth.reconnectRequired")}
						</div>
					) : isConnected ? (
						<div className="flex items-center gap-1 text-sm text-status-success">
							<Icon className="w-4 h-4" icon={faCheck} />
							{ts("connected")}
						</div>
					) : (
						<div className="flex items-center gap-1 text-sm text-gray-500">
							<Icon className="w-4 h-4" icon={faXmark} />
							{ts("notConnected")}
						</div>
					)}
				</div>

				{needsReauthorization && (
					<CrmReconnectBanner
						buttonLabel={ts("oauth.reconnect")}
						description={ts("oauth.reauthDescription")}
						isLoading={isLoadingOAuthUrl}
						title={ts("oauth.reconnectRequired")}
						onReconnect={handleConnectOAuth}
					/>
				)}

				{isConnected ? (
					<>
						<div className="flex border-b border-gray-200 mb-4">
							{tabItems.map((tab) => (
								<button
									key={tab.key}
									className={classNames(
										"px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer",
										currentTab === tab.key
											? "border-brand-light text-brand-light"
											: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
									)}
									type="button"
									onClick={() => handleTabChange(tab.key)}
								>
									{tab.label}
								</button>
							))}
						</div>

						{currentTab === ZohoTabs.CONNECTION && connectionContent}
						{currentTab === ZohoTabs.SETTINGS && settingsContent}
						{currentTab === ZohoTabs.DEAL_CONFIGURATION && <ZohoDealConfigTab />}
						{currentTab === ZohoTabs.USER_MAPPING && <ZohoUserMappingTab />}
						{currentTab === ZohoTabs.SYNC_LOGS && <ZohoSyncLogsTab />}
					</>
				) : (
					connectionContent
				)}
			</div>

			{isFieldMappingOpen && (
				<ZohoFieldMappingModal
					isOpen={isFieldMappingOpen}
					onClose={() => setIsFieldMappingOpen(false)}
				/>
			)}

			{showDisconnectModal && (
				<Modal
					handleClose={() => setShowDisconnectModal(false)}
					handleSave={handleDisconnectOAuth}
					isLoading={isDisconnecting}
					isOpened={showDisconnectModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={ts("disconnect")}
					title={ts("oauth.disconnectTitle")}
				>
					<div className="text-sm">{ts("oauth.disconnectBody")}</div>
				</Modal>
			)}

			{renderMessageSnackbar()}
		</>
	);
};
